import apiClient from './api'
import type {
  PaymentMethod,
  PaymentGateway,
  PaymentReconciliationRequest,
  PaymentReconciliationResponse
} from '../types/payment'

export interface PaymentTransactionCreate {
  amount: number
  payment_method: PaymentMethod
  payment_gateway?: PaymentGateway
  reference_number?: string
  notes?: string
  auto_complete?: boolean
}

export interface PaymentTransaction {
  id: string
  order_id: string
  customer_id: string
  transaction_number: string
  external_transaction_id?: string
  transaction_type: string
  amount: number
  currency: string
  payment_method: PaymentMethod
  payment_gateway?: PaymentGateway
  status: string
  gateway_response?: Record<string, any>
  gateway_transaction_id?: string
  transaction_date: string
  completed_at?: string
  failed_at?: string
  failure_reason?: string
  recorded_by_admin_id?: string
  reference_number?: string
  notes?: string
  transaction_metadata?: Record<string, any>
  created_at: string
  updated_at: string
  recorded_by_admin?: {
    id: string
    name: string
    email: string
  }
}

export interface OrderBalance {
  order_id: string
  order_number: string
  total_amount: number
  paid_amount: number
  remaining_balance: number
  payment_status: string
  payment_due_date?: string
  credit_terms_days: number
  transaction_count: number
  transactions: PaymentTransaction[]
}

export interface AgingReport {
  customer_id: string
  customer_name?: string
  customer_email?: string
  total_outstanding: number
  overdue_amount: number
  aging_buckets: {
    aging_0_30_days: number
    aging_31_60_days: number
    aging_61_90_days: number
    aging_over_90_days: number
  }
  calculated_at: string
}

export interface AgingReportList {
  reports: AgingReport[]
  total_customers: number
  total_outstanding: number
  total_overdue: number
  generated_at: string
}

export interface CreditAccount {
  customer_id: string
  customer_name?: string
  customer_email?: string
  credit_limit: number
  available_credit: number
  total_outstanding: number
  overdue_amount: number
  credit_utilization_percent: number
  default_payment_terms_days: number
  credit_hold: boolean
  credit_hold_reason?: string
  credit_hold_date?: string
  aging_buckets: {
    aging_0_30_days: number
    aging_31_60_days: number
    aging_61_90_days: number
    aging_over_90_days: number
  }
  last_payment_date?: string
  last_payment_amount: number
  created_at: string
  updated_at: string
}

export interface CreditUpdate {
  credit_limit?: number
  payment_terms_days?: number
  reason?: string
}

export interface CreditHoldRequest {
  reason: string
  notes?: string
}

export interface CreditHoldReleaseRequest {
  notes?: string
}

export const paymentService = {
  // Record payment for an order
  recordPayment: async (orderId: string, paymentData: PaymentTransactionCreate): Promise<PaymentTransaction> => {
    const response = await apiClient.post(`/admin/orders/${orderId}/payments`, paymentData)
    return response.data
  },

  // Get payment history for an order
  getOrderPayments: async (orderId: string): Promise<PaymentTransaction[]> => {
    const response = await apiClient.get(`/admin/orders/${orderId}/payments`)
    return response.data
  },

  // Get order balance
  getOrderBalance: async (orderId: string): Promise<OrderBalance> => {
    const response = await apiClient.get(`/admin/orders/${orderId}/balance`)
    return response.data
  },

  // Payment reconciliation
  reconcilePayments: async (request: PaymentReconciliationRequest): Promise<PaymentReconciliationResponse> => {
    const response = await apiClient.post('/admin/payments/reconcile', request)
    return response.data
  },

  // Get aging report
  getAgingReport: async (): Promise<AgingReportList> => {
    const response = await apiClient.get('/admin/payments/aging-report')
    return response.data
  },

  // Get customer credit account
  getCustomerCredit: async (customerId: string): Promise<CreditAccount> => {
    const response = await apiClient.get(`/admin/customers/${customerId}/credit`)
    return response.data
  },

  // Update customer credit
  updateCustomerCredit: async (customerId: string, creditData: CreditUpdate): Promise<CreditAccount> => {
    const response = await apiClient.patch(`/admin/customers/${customerId}/credit`, creditData)
    return response.data
  },

  // Apply credit hold
  applyCreditHold: async (customerId: string, holdData: CreditHoldRequest): Promise<any> => {
    const response = await apiClient.post(`/admin/customers/${customerId}/credit/hold`, holdData)
    return response.data
  },

  // Release credit hold
  releaseCreditHold: async (customerId: string, releaseData: CreditHoldReleaseRequest): Promise<any> => {
    const response = await apiClient.post(`/admin/customers/${customerId}/credit/release`, releaseData)
    return response.data
  },

  // Export aging report to CSV
  exportAgingReport: async (): Promise<void> => {
    const response = await apiClient.get('/admin/payments/aging-report/export', {
      responseType: 'blob'
    })

    const blob = new Blob([response.data], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `aging_report_${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }
}
